import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { editorLayerName, editorOptionsMap, ElementAttribute, ElementTagName } from '@models/editor';
import { CanvasService } from '@service/canvas/canvas.service';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import { EditorService } from '@service/editor.service';
import { firstValueFrom, fromEvent, lastValueFrom, Subject, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { DuplicateMimicDialogComponent } from '../duplicate-mimic-dialog/duplicate-mimic-dialog.component';
import { DuplicateItemType } from '@helpers/constants';
import { DialogOptions, SimpleOverlayComponent } from '@components/simple-overlay/simple-overlay.component';

export interface Layer {
    tag: string;
    id: string;
    layerName: string;
    type?: string;
    connectorType?: string;
}

@Component({
    selector: 'app-layers-menu',
    templateUrl: './layers-menu.component.html',
    styleUrls: ['./layers-menu.component.css'],
    standalone: false,
})
export class LayersMenuComponent implements OnInit, AfterViewChecked, OnDestroy {
    @Input() groupElementId;
    @ViewChildren('otherOptions') otherOptions: QueryList<ElementRef>;
    @ViewChild('simpleOverlay') simpleOverlay: SimpleOverlayComponent;

    currentLayers: Layer[];
    selectedElement: any;
    selectedLayer: Layer;

    showElements: boolean = false;
    visibleOptions: number;

    optionsTop: string;
    optionsLeft: string;

    private destroySubscriptionSubject$ = new Subject<void>();

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (isNaN(this.visibleOptions)) return;
        const isInsideAny = this.otherOptions.toArray().some(el => el.nativeElement.contains(event.target));
        if (!isInsideAny) {
            this.visibleOptions = null;
        }
    }

    @HostListener('window:resize')
    onWindowChange() {
        this.visibleOptions = null;
    }

    constructor(
        private canvasService: CanvasService,
        private cdr: ChangeDetectorRef,
        private editorService: EditorService,
        private selectedElementsService: SelectedElementsService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        const scrollContainer = document.querySelector('.layers-container');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroySubscriptionSubject$))
                .subscribe(() => {
                    this.visibleOptions = null;
                });
        }

        this.editorService.currentMimic$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(() => {
            if (!this.groupElementId) {
                this.getLayers();
            }
        });

        this.selectedElementsService
            .onElementsSelected()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (elem: SVGGraphicsElement[]) => {
                if (elem.length === 1) {
                    this.selectedElement = elem[0];
                    this.cdr.detectChanges();
                } else {
                    this.selectedElement = null;
                }
                this.updateSelectedLayer();
            });
    }

    ngAfterViewChecked(): void {
        this.getLayers();
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    getLayers() {
        let groupElement;
        const svgContent = this.canvasService.getSvgContent();
        if (!this.groupElementId) {
            groupElement = svgContent.querySelector('g.layer');
        } else {
            groupElement = svgContent.querySelector(`#${this.groupElementId}`);
        }

        const layers = this.parseSVGElements(groupElement);
        if (_.isEqual(this.currentLayers, layers)) {
            return;
        }
        this.currentLayers = _.cloneDeep(layers);
        this.updateSelectedLayer();
        this.cdr.detectChanges();
    }

    getMatIcon(layer: Layer) {
        if (layer.type) {
            return editorOptionsMap.get(layer.type) || layer.type;
        }
        if (layer.connectorType) {
            const connectorIconMap = {
                straight: 'line-icon',
                corner: 'corner-icon',
                direction: 'direction-icon',
                endpoint: 'end-point-icon',
                junction: 'cross-icon',
            };
            return connectorIconMap[layer.connectorType];
        }
        if (layer.tag !== ElementTagName.foreignObject && layer.tag !== ElementTagName.g) {
            return editorOptionsMap.get(layer.tag);
        }
        if (layer.tag === ElementTagName.g) {
            return 'group';
        }
    }

    selectLayer(layer: Layer) {
        const svgContent = this.canvasService.getSvgContent();
        const element: any = svgContent.querySelector(`#${layer.id}`);
        if (element) {
            this.selectedElement = element;
            this.selectedElementsService.setSelectedElements([element]);
        }
        if (layer.tag === ElementTagName.g) {
            if (layer?.id == this.selectedLayer?.id) this.showElements = true;
            else this.showElements = !this.showElements;
        }

        this.cdr.detectChanges();
    }

    isChildOfId(targetId, layerId) {
        let groupElement;
        const svgContent = this.canvasService.getSvgContent();
        groupElement = svgContent.querySelector(`#${layerId}`);

        if (!groupElement) return false;

        const targetElement = groupElement.querySelector(`#${targetId}`);

        if (targetElement) {
            return targetElement.parentElement !== null || targetElement.parentNode !== null;
        }

        return false;
    }

    isGroup(layer: Layer): boolean {
        return !layer.type && !layer.connectorType && layer.tag === ElementTagName.g;
    }

    shouldShowElements(layer: Layer) {
        if (!this.showElements || layer.type || layer.connectorType) return false;
        if (layer.tag !== ElementTagName.g) return false;
        if (!this.selectedElement) return false;

        if (layer?.id == this.selectedLayer?.id) return true;

        const found = this.isChildOfId(this.selectedElement?.getAttribute(ElementAttribute.id), layer.id);

        return found && this.showElements;
    }

    parseSVGElements(element) {
        const result: Layer[] = [];
        if (!element) {
            return result;
        }

        const counter = {};
        for (let child of element.children) {
            if (child.tagName === ElementTagName.title) continue;
            const childObject: Layer = {
                tag: child.tagName,
                id: null,
                layerName: null,
            };

            for (let attr of child.attributes) {
                if (attr.name === ElementAttribute.id) {
                    childObject.id = attr.value;
                } else if (attr.name === ElementAttribute.type) {
                    childObject.type = attr.value;
                } else if (attr.name === ElementAttribute.connectorType) {
                    childObject.connectorType = attr.value;
                } else if (attr.name === ElementAttribute.iCreateType) {
                    childObject.type = attr.value;
                }
            }

            const layerName = child.getAttribute(ElementAttribute.layerName);
            if (layerName) {
                childObject.layerName = layerName;
            } else {
                if (childObject.connectorType) {
                    const connectorIconMap = {
                        straight: 'Straight con.',
                        corner: 'Corner con.',
                        direction: 'Direction con.',
                        endpoint: 'Endpoint con.',
                        junction: 'Junction con.',
                    };
                    const layerName = connectorIconMap[childObject.connectorType];
                    if (!counter[layerName]) counter[layerName] = 0;
                    counter[layerName] += 1;
                    childObject.layerName = `${layerName} ${counter[layerName]}`;
                } else {
                    const layerName = editorLayerName.get(childObject.type);
                    if (layerName) {
                        if (!counter[layerName]) counter[layerName] = 0;
                        counter[layerName] += 1;
                        childObject.layerName = `${layerName} ${counter[layerName]}`;
                    } else {
                        const tagName = child.tagName.charAt(0).toUpperCase() + child.tagName.slice(1);
                        if (!counter[tagName]) counter[tagName] = 0;
                        counter[tagName] += 1;
                        childObject.layerName = `${tagName} ${counter[tagName]}`;
                    }
                }
            }
            result.push(childObject);
        }

        return result;
    }

    updateSelectedLayer() {
        if (!this.selectedElement) this.selectedLayer = null;
        this.selectedLayer = this.currentLayers.find((layer: Layer) => this.isSelectedElement(layer));
    }

    isSelectedElement(layer: Layer) {
        if (!this.selectedElement) return false;
        return layer.id == this.selectedElement?.getAttribute('id');
    }

    getLayerName(layer: Layer) {
        const layerName = layer?.layerName;
        return layerName || layer?.id;
    }

    async openOptions(index, layer) {
        this.visibleOptions = index;
        this.updatePosition(index);
        const dialogRef = this.dialog.open(SimpleOverlayComponent, {
            data: {
                options: [DialogOptions.EDIT],
            },
            backdropClass: 'transparent-backdrop',
            position: {
                top: `${this.optionsTop}`,
                left: `${this.optionsLeft}`,
            },
            disableClose: false,
        });

        const selectedOption = await lastValueFrom(dialogRef.afterClosed());
        if (!selectedOption) return;
        if (selectedOption == DialogOptions.EDIT) this.editLayerName(layer);
    }

    private updatePosition(index) {
        if (!this.otherOptions) {
            this.optionsTop = null;
            this.optionsLeft = null;
            return;
        }
        const optionsColArray = this.otherOptions.toArray();
        const selectedUnit = optionsColArray ? optionsColArray[index] : null;
        if (!selectedUnit) {
            this.optionsTop = null;
            this.optionsLeft = null;
            return;
        }

        const selectorPosition = selectedUnit.nativeElement.getBoundingClientRect();

        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        let top = selectorPosition.bottom + scrollTop;

        this.optionsTop = top - 29 + 'px';
        this.optionsLeft = selectorPosition.left + scrollLeft + 26 + 'px';

        this.cdr.detectChanges();
    }

    async editLayerName(layer: Layer) {
        const existingNames = this.currentLayers.map(layer => layer.layerName) || [];
        try {
            const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                data: {
                    title: 'mimic-drawing.layers.edit.title',
                    initialName: layer.layerName,
                    existingNames,
                    type: DuplicateItemType.LAYER,
                    submitButtonLabel: 'dlg.edit',
                    autoIncrementName: false,
                },
            });
            const result = await firstValueFrom(dialogRef.afterClosed());
            if (!result.name) return;
            const svgContent = this.canvasService.getSvgContent();
            const element = svgContent.querySelector(`#${layer.id}`);
            if (!element) return;
            element.setAttribute(ElementAttribute.layerName, result.name);
            this.currentLayers = this.currentLayers.map(currentLayer => {
                if (currentLayer.id == layer.id) currentLayer.layerName = result.name;
                return currentLayer;
            });
        } catch (error) {
            console.log(error);
        }
    }
}
